/** GLSL ES 3.0 shaders for the WebGL 2 backend. All positions are view-relative (§4.2). */

/** Anti-aliased coverage of a point inside a frame clip rect (1 inside, 0 outside, smooth over one device pixel). */
/** Dark canvas: invert 93 % + hue 180° on premultiplied color (see inkRgb in color.ts). */
const INK_FN = /* glsl */ `
uniform float u_dark;
vec4 ink(vec4 c) {
  if (u_dark < 0.5 || c.a <= 0.0) return c;
  vec3 s = vec3(0.93) - 0.86 * (c.rgb / c.a);
  s = clamp(mat3(-0.574, 0.426, 0.426, 1.43, 0.43, 1.43, 0.144, 0.144, -0.856) * s, 0.0, 1.0);
  return vec4(s * c.a, c.a);
}`;

const CLIP_FN = /* glsl */ `
float clipCoverage(vec2 w, vec4 c, float scale) {
  vec2 lo = clamp((w - c.xy) * scale + 0.5, 0.0, 1.0);
  vec2 hi = clamp((c.zw - w) * scale + 0.5, 0.0, 1.0);
  return lo.x * lo.y * hi.x * hi.y;
}`;

export const SHAPE_VS = /* glsl */ `#version 300 es
precision highp float;
layout(location=0) in vec2 a_corner;
layout(location=1) in vec2 a_center;   // object center - camera center (dynamic)
layout(location=2) in vec4 a_geom;     // offset.xy (rotated, from object center), half size.xy
layout(location=3) in vec4 a_misc;     // rotation, kind, radius, stroke width
layout(location=4) in vec4 a_fill;     // premultiplied
layout(location=5) in vec4 a_fill2;    // pattern background, premultiplied
layout(location=6) in vec4 a_stroke;   // premultiplied
layout(location=7) in vec4 a_params;   // fill kind, dash kind, pattern spacing, pattern angle
layout(location=8) in vec2 a_pat;      // primitive center in object-local coords
layout(location=9) in vec4 a_clip;     // frame clip rect (view-relative x0,y0,x1,y1)
uniform vec2 u_viewport;
uniform float u_scale;
out vec2 v_local;
out vec2 v_world;
flat out vec4 v_clip;
flat out vec4 v_misc;
flat out vec2 v_hs;
flat out vec4 v_fill;
flat out vec4 v_fill2;
flat out vec4 v_stroke;
flat out vec4 v_params;
flat out vec2 v_pat;
void main() {
  vec2 hs = a_geom.zw;
  float pad = a_misc.w * 0.5 + 2.0 / u_scale;
  vec2 local = a_corner * (hs + vec2(pad));
  float c = cos(a_misc.x);
  float s = sin(a_misc.x);
  vec2 w = a_center + a_geom.xy + vec2(local.x * c - local.y * s, local.x * s + local.y * c);
  vec2 px = w * u_scale;
  gl_Position = vec4(px.x * 2.0 / u_viewport.x, -px.y * 2.0 / u_viewport.y, 0.0, 1.0);
  v_world = w;
  v_clip = a_clip;
  v_local = local;
  v_misc = a_misc;
  v_hs = hs;
  v_fill = a_fill;
  v_fill2 = a_fill2;
  v_stroke = a_stroke;
  v_params = a_params;
  v_pat = a_pat;
}`;

export const SHAPE_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_local;
flat in vec4 v_misc;
flat in vec2 v_hs;
flat in vec4 v_fill;
flat in vec4 v_fill2;
flat in vec4 v_stroke;
flat in vec4 v_params;
flat in vec2 v_pat;
in vec2 v_world;
flat in vec4 v_clip;
uniform float u_scale;
out vec4 o;
const float PI = 3.14159265359;
${CLIP_FN}
${INK_FN}

float sdRoundBox(vec2 p, vec2 b, float r) {
  r = min(r, min(b.x, b.y));
  vec2 q = abs(p) - b + r;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

float sdEllipse(vec2 p, vec2 ab) {
  ab = max(ab, vec2(1e-6));
  float k0 = length(p / ab);
  float k1 = length(p / (ab * ab));
  if (k0 < 1e-6) return -min(ab.x, ab.y);
  return k0 * (k0 - 1.0) / k1;
}

// Polygon SDF (negative inside) + perimeter parameter t starting at v0 (matches Canvas path order).
float sdPoly(vec2 p, vec2 v0, vec2 v1, vec2 v2, vec2 v3, int n, out float t) {
  vec2 v[4];
  v[0] = v0; v[1] = v1; v[2] = v2; v[3] = v3;
  float best = 1e30;
  float sgn = 1.0;
  float acc = 0.0;
  t = 0.0;
  for (int i = 0; i < 4; i++) {
    if (i >= n) break;
    vec2 a = v[i];
    vec2 b = v[(i + 1) % n];
    vec2 e = b - a;
    float len = length(e);
    vec2 w = p - a;
    float h = clamp(dot(w, e) / max(dot(e, e), 1e-12), 0.0, 1.0);
    vec2 q = w - e * h;
    float d2 = dot(q, q);
    if (d2 < best) { best = d2; t = acc + h * len; }
    acc += len;
    vec2 e2 = a - b;
    vec2 w2 = p - b;
    bvec3 c = bvec3(p.y >= b.y, p.y < a.y, e2.x * w2.y > e2.y * w2.x);
    if (all(c) || all(not(c))) sgn = -sgn;
  }
  return sgn * sqrt(best);
}

void main() {
  vec2 p = v_local;
  vec2 hs = v_hs;
  int kind = int(v_misc.y + 0.5);
  float radius = v_misc.z;
  float sw = v_misc.w;
  float aa = 1.0 / u_scale;
  float d;
  float t;
  if (kind == 0) {
    float dp = sdPoly(p, vec2(-hs.x, -hs.y), vec2(hs.x, -hs.y), hs, vec2(-hs.x, hs.y), 4, t);
    d = radius > 0.0 ? sdRoundBox(p, hs, radius) : dp;
  } else if (kind == 1) {
    d = sdEllipse(p, hs);
    float a = atan(p.y / max(hs.y, 1e-6), p.x / max(hs.x, 1e-6));
    if (a < 0.0) a += 2.0 * PI;
    float A = hs.x, B = hs.y;
    float per = PI * (3.0 * (A + B) - sqrt((3.0 * A + B) * (A + 3.0 * B)));
    t = a / (2.0 * PI) * per;
  } else if (kind == 2) {
    d = sdPoly(p, vec2(0.0, -hs.y), vec2(hs.x, hs.y), vec2(-hs.x, hs.y), vec2(0.0), 3, t);
  } else {
    d = sdPoly(p, vec2(0.0, -hs.y), vec2(hs.x, 0.0), vec2(0.0, hs.y), vec2(-hs.x, 0.0), 4, t);
  }

  // fill
  int fillKind = int(v_params.x + 0.5);
  vec4 fillC = vec4(0.0);
  if (fillKind == 1) {
    fillC = v_fill;
  } else if (fillKind >= 2) {
    float sp = max(v_params.z, 1e-6);
    float th = max(sp * 0.12, 0.5);
    vec2 lp = p + v_pat;
    float m;
    if (fillKind == 2) {
      vec2 n = vec2(cos(v_params.w), sin(v_params.w));
      float dd = dot(lp, n);
      m = abs(fract(dd / sp + 0.5) - 0.5) * sp;
    } else {
      vec2 g = abs(fract(lp / sp + 0.5) - 0.5) * sp;
      m = min(g.x, g.y);
    }
    float lineMask = clamp(0.5 - (m - th * 0.5) / aa, 0.0, 1.0);
    fillC = v_fill2 * (1.0 - lineMask) + v_fill * lineMask;
    // too dense to resolve on screen: fade to the average tone instead of moire
    float pxSp = sp * u_scale;
    if (pxSp < 6.0) {
      float density = clamp(fillKind == 3 ? 2.0 * th / sp : th / sp, 0.0, 1.0);
      vec4 avg = v_fill2 * (1.0 - density) + v_fill * density;
      fillC = mix(avg, fillC, clamp((pxSp - 3.0) / 3.0, 0.0, 1.0));
    }
  }
  float fillA = clamp(0.5 - d / aa, 0.0, 1.0);
  vec4 F = fillC * fillA;

  // stroke
  vec4 S = vec4(0.0);
  if (sw > 0.0 && v_stroke.a > 0.0) {
    float swEff = max(sw, aa);
    float thin = sw < aa ? sw / aa : 1.0;
    int dash = int(v_params.y + 0.5);
    float sa;
    float swd = max(sw, 0.5);
    if (dash == 2) {
      float period = swd * 2.0;
      float u = mod(t, period);
      float du = min(u, period - u);
      float dist = length(vec2(du, abs(d))) - swEff * 0.5;
      sa = clamp(0.5 - dist / aa, 0.0, 1.0);
    } else {
      sa = clamp(0.5 - (abs(d) - swEff * 0.5) / aa, 0.0, 1.0);
      if (dash == 1) {
        float on = swd * 4.0;
        float period = swd * 6.5;
        float u = mod(t, period);
        float edge = min(u, on - u);
        sa *= clamp(0.5 + edge / aa, 0.0, 1.0);
      } else if (dash == 3) {
        float on = swd * 1.2;
        float period = swd * 2.4;
        float u = mod(t, period);
        float edge = min(u, on - u);
        sa *= clamp(0.5 + edge / aa, 0.0, 1.0);
      }
    }
    S = v_stroke * sa * thin;
  }
  o = ink(S + F * (1.0 - S.a)) * clipCoverage(v_world, v_clip, u_scale);
  if (o.a < 0.002) discard;
}`;

export const MESH_VS = /* glsl */ `#version 300 es
precision highp float;
layout(location=0) in vec3 a_pos;      // xy, depth (0..1): one value per mesh, later meshes nearer
layout(location=1) in vec4 a_color;
layout(location=2) in vec2 a_center;
layout(location=3) in vec4 a_clip;
uniform vec2 u_viewport;
uniform float u_scale;
out vec4 v_color;
out vec2 v_world;
flat out vec4 v_clip;
void main() {
  vec2 w = a_center + a_pos.xy;
  vec2 px = w * u_scale;
  gl_Position = vec4(px.x * 2.0 / u_viewport.x, -px.y * 2.0 / u_viewport.y, a_pos.z * 2.0 - 1.0, 1.0);
  v_color = a_color;
  v_world = w;
  v_clip = a_clip;
}`;

export const MESH_FS = /* glsl */ `#version 300 es
precision highp float;
in vec4 v_color;
in vec2 v_world;
flat in vec4 v_clip;
uniform float u_scale;
uniform float u_hl;   // highlighter: 1 darken (MIN blend), 2 lighten (MAX blend)
out vec4 o;
${CLIP_FN}
${INK_FN}
void main() {
  float cov = clipCoverage(v_world, v_clip, u_scale);
  if (u_hl > 0.5) {
    if (v_color.a < 0.002 || cov < 0.002) discard;
    // straight color seen through its opacity: over white for darken, over black for lighten
    vec3 c = u_hl < 1.5 ? vec3(1.0) - vec3(v_color.a) + v_color.rgb : v_color.rgb;
    o = vec4(u_hl < 1.5 ? mix(vec3(1.0), c, cov) : c * cov, 1.0);
    return;
  }
  o = ink(v_color) * cov;
  if (o.a < 0.002) discard;
}`;

export const TEX_VS = /* glsl */ `#version 300 es
precision highp float;
layout(location=0) in vec2 a_pos;
layout(location=1) in vec3 a_uva;
layout(location=2) in vec2 a_center;
layout(location=3) in vec4 a_clip;
uniform vec2 u_viewport;
uniform float u_scale;
out vec2 v_uv;
out float v_alpha;
out vec2 v_world;
flat out vec4 v_clip;
void main() {
  vec2 w = a_center + a_pos;
  vec2 px = w * u_scale;
  gl_Position = vec4(px.x * 2.0 / u_viewport.x, -px.y * 2.0 / u_viewport.y, 0.0, 1.0);
  v_uv = a_uva.xy;
  v_alpha = a_uva.z;
  v_world = w;
  v_clip = a_clip;
}`;

export const TEX_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_uv;
in float v_alpha;
in vec2 v_world;
flat in vec4 v_clip;
uniform sampler2D u_tex;
uniform float u_scale;
out vec4 o;
${CLIP_FN}
${INK_FN}
void main() {
  o = ink(texture(u_tex, v_uv)) * v_alpha * clipCoverage(v_world, v_clip, u_scale);
  if (o.a < 0.002) discard;
}`;

export const GRID_VS = /* glsl */ `#version 300 es
precision highp float;
const vec2 P[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
void main() { gl_Position = vec4(P[gl_VertexID], 0.0, 1.0); }`;

export const GRID_FS = /* glsl */ `#version 300 es
precision highp float;
uniform vec2 u_viewport;   // device px
uniform float u_scale;     // device px per world unit
uniform vec2 u_origin;     // world coords of the top-left pixel, reduced modulo the major step
uniform float u_step;
uniform vec4 u_color;      // premultiplied
uniform int u_kind;        // 0 dots, 1 lines, 2 cells
uniform float u_dpr;
out vec4 o;
void main() {
  vec2 pix = vec2(gl_FragCoord.x, u_viewport.y - gl_FragCoord.y);
  vec2 world = u_origin + pix / u_scale;
  vec2 g = abs(fract(world / u_step + 0.5) - 0.5) * u_step * u_scale;
  float a;
  if (u_kind == 0) {
    float r = clamp(u_step * u_scale / 12.0, u_dpr, 2.0 * u_dpr) * 0.6;
    a = clamp(r + 0.5 - length(g), 0.0, 1.0);
  } else {
    float lw = 0.5 * u_dpr;
    float minor = clamp(lw + 0.5 - min(g.x, g.y), 0.0, 1.0);
    if (u_kind == 1) {
      a = minor * 0.7;
    } else {
      float big = u_step * 5.0;
      vec2 G = abs(fract(world / big + 0.5) - 0.5) * big * u_scale;
      float major = clamp(lw + 0.5 - min(G.x, G.y), 0.0, 1.0);
      a = max(minor * 0.45, major);
    }
  }
  if (a <= 0.0) discard;
  o = u_color * a;
}`;
